import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { SHIPYARD_NAMES, getShipyardLabel, getShipyardImageSrc } from '@/lib/shipyards'
import { projectsApi, shipyardLogoUrl, type Shipyard } from '@/lib/projectsApi'
import { FileDown, RefreshCw } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { deduplicateSystems } from '@/lib/tpUtils'
import { useTPWorkbook } from '@/hooks/useTPWorkbook'
import { TPWorkbookUpload } from '@/components/tp/TPWorkbookUpload'
import { TPClassSelector } from '@/components/tp/TPClassSelector'
import type { TPClass } from '@/lib/tpWorkbookParserClass'
import { useTranslation } from 'react-i18next'

function getApiUrl() {
  if (typeof window !== 'undefined' && window.location.origin) {
    return `${window.location.origin}/api/generate-tp`
  }
  return '/api/generate-tp'
}

export const GenerateTPPage: React.FC = () => {
  const { t } = useTranslation()
  const [shipyard, setShipyard] = useState('')
  const [shipyardProject, setShipyardProject] = useState('')
  const [shipowner, setShipowner] = useState('')
  const [vesselType, setVesselType] = useState('')
  const [systems, setSystems] = useState<string[]>([])
  const [selectedSystem, setSelectedSystem] = useState('')
  const [loadingSystems, setLoadingSystems] = useState(false)
  const [systemsLoaded, setSystemsLoaded] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tpClass, setTpClass] = useState<TPClass>('DNV')
  // the picker follows the shipyards table so yards added under Settings are
  // selectable here too; the built-in name list stands in if the call fails
  const [dbShipyards, setDbShipyards] = useState<Shipyard[]>([])

  useEffect(() => {
    projectsApi.listShipyards().then(setDbShipyards).catch(() => {})
  }, [])

  const shipyardOptions: string[] = dbShipyards.length > 0
    ? dbShipyards.map(s => s.name)
    : SHIPYARD_NAMES

  // re-parses with correct parser when class changes
  const wb = useTPWorkbook(tpClass)

  const handleLoadSystems = async () => {
    if (!shipyard.trim() || !shipyardProject.trim()) {
      setError(t('generateTP.requiredProject'))
      return
    }
    setError(null)
    setLoadingSystems(true)
    setSystems([])
    setSelectedSystem('')
    setSystemsLoaded(false)
    try {
      const { data, error: dbErr } = await supabase
        .from('systems_190')
        .select('system')
        .eq('shipyard', shipyard.trim())
        .eq('shipyard_project', shipyardProject.trim())
        .not('system', 'is', null)

      if (dbErr) throw dbErr

      const unique = deduplicateSystems(data ?? [])

      setSystems(unique)
      setSystemsLoaded(true)
      if (unique.length === 0) setError(t('generateTP.noSystems'))
    } catch (err: any) {
      setError(err.message || t('generateTP.loadFailed'))
    } finally {
      setLoadingSystems(false)
    }
  }

  // shared fetch for both generate paths. includeSections=true for main path
  const doGenerate = async (includeSections: boolean) => {
    if (!selectedSystem) {
      setError(t('generateTP.selectSystemError'))
      return
    }
    setError(null)
    setGenerating(true)
    try {
      const tp_sections = includeSections && wb.workbookResult
        ? wb.workbookResult.sections
        : undefined

      const res = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipyard: shipyard.trim(),
          shipyard_project: shipyardProject.trim(),
          system_name: selectedSystem,
          shipowner: shipowner.trim(),
          vessel_type: vesselType.trim(),
          ...(tp_sections ? { tp_sections } : {}),
          tp_class: tpClass,
        }),
      })

      const disposition = res.headers.get('Content-Disposition')
      let filename = 'TP.docx'
      if (disposition) {
        const match = /filename="?([^";]+)"?/.exec(disposition)
        if (match) filename = match[1].trim()
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.details || data.error || `Request failed (${res.status})`)
        return
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message || t('generateTP.generateFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerate      = () => doGenerate(true)
  const handleGenerateDraft = () => doGenerate(false)

  const resetSystems = () => {
    setSystems([])
    setSelectedSystem('')
    setSystemsLoaded(false)
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white p-4">
      <div className="max-w-lg mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5" />
              {t('generateTP.title')}
            </CardTitle>
            <CardDescription>
              {t('generateTP.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <TPClassSelector
              value={tpClass}
              onChange={(cls) => { setTpClass(cls); wb.resetWorkbook() }}
            />
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                {t('generateTP.shipyard')} <span className="text-red-500">*</span>
              </label>
              <select
                value={shipyard}
                onChange={(e) => { setShipyard(e.target.value); resetSystems() }}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">{t('generateTP.selectShipyard')}</option>
                {shipyardOptions.map((name) => (
                  <option key={name} value={name}>{getShipyardLabel(name)}</option>
                ))}
              </select>
            </div>

            {/* Shipyard logo preview */}
            {shipyard && (() => {
              // same precedence the document generator uses: uploaded logo
              // first, bundled asset second — so this preview is the truth
              const row = dbShipyards.find(s => s.name === shipyard)
              const logoSrc = shipyardLogoUrl(row) ?? getShipyardImageSrc(shipyard)
              return (
                <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-sm">
                  {logoSrc ? (
                    <>
                      <img
                        src={logoSrc}
                        alt={`${shipyard} logo`}
                        className="h-7 max-w-[120px] object-contain"
                      />
                      <span className="text-slate-500 dark:text-slate-400 text-xs">
                        {t('generateTP.logoConfigured')}
                      </span>
                    </>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-400 text-xs">
                      {t('generateTP.noLogo')}
                    </span>
                  )}
                </div>
              )
            })()}

            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                {t('generateTP.shipyardProject')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={shipyardProject}
                onChange={(e) => { setShipyardProject(e.target.value); resetSystems() }}
                placeholder={t('generateTP.projectPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                {t('generateTP.shipOwner')}
              </label>
              <Input
                type="text"
                value={shipowner}
                onChange={(e) => setShipowner(e.target.value)}
                placeholder={t('generateTP.shipOwnerPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                {t('generateTP.vesselType')}
              </label>
              <Input
                type="text"
                value={vesselType}
                onChange={(e) => setVesselType(e.target.value)}
                placeholder={t('generateTP.vesselTypePlaceholder')}
              />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleLoadSystems}
              disabled={loadingSystems || !shipyard || !shipyardProject}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingSystems ? 'animate-spin' : ''}`} />
              {loadingSystems ? t('generateTP.loading') : systemsLoaded ? t('generateTP.reloadSystems') : t('generateTP.loadSystems')}
            </Button>

            {!systemsLoaded && error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </CardContent>
        </Card>

        {systemsLoaded && systems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('generateTP.selectSystem')}</CardTitle>
              <CardDescription>
                {t('generateTP.uniqueSystems', { count: systems.length })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                {systems.map((sys) => (
                  <label
                    key={sys}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer border transition-colors ${
                      selectedSystem === sys
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <input
                      type="radio"
                      name="system"
                      value={sys}
                      checked={selectedSystem === sys}
                      onChange={() => { setSelectedSystem(sys); setError(null) }}
                      className="accent-blue-600"
                    />
                    <span className="text-sm">{sys}</span>
                  </label>
                ))}
              </div>

              {/* workbook upload, required for full generation */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                <TPWorkbookUpload hook={wb} />
              </div>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              {/* Primary: requires parsed workbook */}
              <Button
                onClick={handleGenerate}
                disabled={generating || !selectedSystem || !wb.workbookResult}
                className="w-full"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {generating ? t('generateTP.generating') : t('generateTP.generateDocument')}
              </Button>

              {/* Secondary draft path: no workbook required */}
              {!wb.workbookResult && (
                <button
                  type="button"
                  onClick={handleGenerateDraft}
                  disabled={generating || !selectedSystem}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed underline underline-offset-2 text-center pt-0.5"
                >
                  {generating ? t('generateTP.generating') : t('generateTP.generateDraft')}
                </button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
